import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Clock } from "lucide-react";
import { cn } from "@/lib/cn";
import { Avatar, AvatarFallback, getInitials } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { LeadSourceBadge } from "@/components/ui/LeadSourceBadge";

/**
 * DealCard — Kanban kartochkasi.
 * Variantlar:
 *   - compact: 32-48px (zich list)
 *   - default: 96-120px (asosiy)
 *   - expanded: 160px+ (ko'p ma'lumot)
 *
 * Visual states:
 *   - idle, hover (border lift), active drag (isDragging)
 *
 * Status ribbonlari (chap chetda 3px gradient strip):
 *   - WON  → emerald gradient (success glow)
 *   - LOST → rose gradient    (danger glow)
 *   - OPEN → ribbon yo'q
 */

const cardVariants = cva(
  "group relative w-full overflow-hidden rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-3 text-left transition-all duration-150",
  {
    variants: {
      variant: {
        compact: "py-2",
        default: "",
        expanded: "p-4",
      },
      isDragging: {
        true: "scale-[1.02] cursor-grabbing border-[var(--accent-violet)] shadow-[var(--shadow-strong,0_12px_28px_rgba(0,0,0,0.5))]",
        false:
          "cursor-grab hover:border-[var(--border-strong)] hover:bg-[var(--bg-surface)] hover:-translate-y-px",
      },
      status: {
        OPEN: "",
        WON: "shadow-[var(--status-won-glow)]",
        LOST: "shadow-[var(--status-lost-glow)]",
      },
    },
    defaultVariants: { variant: "default", isDragging: false, status: "OPEN" },
  },
);

export interface DealCardData {
  id: string;
  title: string;
  amount: number;
  currency: string;
  stageEnteredAt?: string | null;
  assignee?: { id: string; name: string; avatarUrl?: string | null } | null;
  source?: string | null;
  status?: "OPEN" | "WON" | "LOST";
}

export interface DealCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick">,
    VariantProps<typeof cardVariants> {
  deal: DealCardData;
  isDragging?: boolean;
  onClick?: (deal: DealCardData) => void;
}

function formatAmountCompact(amount: number, currency: string): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M ${currency}`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K ${currency}`;
  return `${amount} ${currency}`;
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.floor(ms / 86_400_000);
}

function timerColor(days: number): string {
  if (days >= 8) return "text-[var(--accent-rose-fg)]";
  if (days >= 4) return "text-[var(--accent-amber-fg)]";
  return "text-[var(--text-secondary)]";
}

export const DealCard = forwardRef<HTMLDivElement, DealCardProps>(function DealCard(
  { deal, variant, isDragging, onClick, className, ...rest },
  ref,
) {
  const days = daysSince(deal.stageEnteredAt ?? null);
  const status = deal.status ?? "OPEN";
  const ariaLabel = `${deal.title}, ${formatAmountCompact(deal.amount, deal.currency)}${
    deal.assignee ? `, ${deal.assignee.name}` : ""
  }${status !== "OPEN" ? `, ${status}` : ""}, kartochkani bosing — drawer ochiladi`;

  // Ribbon style — chap chetda 3px vertikal gradient strip (WON/LOST)
  const ribbonBg =
    status === "WON"
      ? "var(--status-won-ribbon)"
      : status === "LOST"
        ? "var(--status-lost-ribbon)"
        : null;

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={() => onClick?.(deal)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(deal);
        }
      }}
      className={cn(cardVariants({ variant, isDragging, status }), className)}
      {...rest}
    >
      {/* Status ribbon (WON/LOST) — chap chetda 3px gradient strip */}
      {ribbonBg && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-[3px] transition-all duration-150 group-hover:w-1"
          style={{ background: ribbonBg }}
        />
      )}

      {/* Header: source + status */}
      {(deal.source || status !== "OPEN") && variant !== "compact" && (
        <div className="mb-1.5 flex items-center justify-between gap-2">
          {deal.source ? <LeadSourceBadge source={deal.source} /> : <span />}
          {status !== "OPEN" && (
            <Badge
              variant={status === "WON" ? "success" : "danger"}
              size="sm"
              dot
            >
              {status}
            </Badge>
          )}
        </div>
      )}

      {/* Title */}
      <div
        className={cn(
          "truncate font-medium text-[var(--text-primary)]",
          variant === "compact" ? "text-xs" : "text-sm",
        )}
        title={deal.title}
      >
        {deal.title}
      </div>

      {/* Footer: amount + timer + assignee */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span
          className={cn(
            "font-mono font-semibold text-[var(--text-primary)]",
            variant === "compact" ? "text-xs" : "text-sm",
          )}
        >
          {formatAmountCompact(deal.amount, deal.currency)}
        </span>
        <div className="flex items-center gap-2">
          {days != null && variant !== "compact" && (
            <span
              className={cn("inline-flex items-center gap-0.5 text-[11px]", timerColor(days))}
              title={`${days} kun ushbu bosqichda`}
            >
              <Clock className="h-3 w-3" aria-hidden />
              {days}k
            </span>
          )}
          {deal.assignee && (
            <Avatar size="xs" title={deal.assignee.name}>
              <AvatarFallback>{getInitials(deal.assignee.name)}</AvatarFallback>
            </Avatar>
          )}
        </div>
      </div>
    </div>
  );
});
